/**
 * What every page of the iManage area reads: the organization, the person's
 * connection and whether they manage the app (IManageAreaContext), and the
 * hook that starts or ends their iManage sign-in. Apart from IManageArea so
 * the area, its landing and its pages can share them without a cycle.
 *
 * @module components/apps/imanage/imanageAreaContext
 */
import type { IManageStatus } from "@amoreg/contracts/imanage";
import * as Schema from "effect/Schema";
import { createContext, useCallback, useContext, useState } from "react";

import { imanageRequest } from "../../../state/imanage";
import { invalidateIManage } from "./imanageQueries";

export interface IManageAreaValue {
  readonly organizationId: string;
  readonly orgSegment: string;
  readonly status: IManageStatus;
  readonly canManage: boolean;
  readonly refreshStatus: () => void;
}

export const IManageAreaContext = createContext<IManageAreaValue | null>(null);

export function useIManageArea(): IManageAreaValue {
  const value = useContext(IManageAreaContext);
  if (!value) throw new Error("useIManageArea outside IManageArea");
  return value;
}

/**
 * Starts the person's iManage sign-in in a new tab. The tab opens before the
 * request so the browser does not treat it as an unrequested pop-up.
 */
export function useIManageConnect(organizationId: string | null, onDone: () => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const connect = useCallback(() => {
    if (!organizationId) return;
    const tab = window.open("about:blank", "_blank");
    if (!tab) {
      setError("Allow a new tab to sign in to iManage.");
      return;
    }
    tab.opener = null;
    setBusy(true);
    setError(null);
    void imanageRequest(organizationId, "connect", {}, {})
      .then((raw) => {
        tab.location.href = Schema.decodeUnknownSync(
          Schema.Struct({ redirectUrl: Schema.String }),
        )(raw).redirectUrl;
      })
      .catch((e: unknown) => {
        tab.close();
        setError(e instanceof Error ? e.message : "iManage sign-in could not start.");
      })
      .finally(() => {
        setBusy(false);
        onDone();
      });
  }, [organizationId, onDone]);
  const disconnect = useCallback(async () => {
    if (!organizationId) return;
    setBusy(true);
    setError(null);
    try {
      await imanageRequest(organizationId, "disconnect", {}, {});
      invalidateIManage(organizationId);
    } catch (e) {
      setError(e instanceof Error ? e.message : "iManage could not be disconnected.");
    } finally {
      setBusy(false);
      onDone();
    }
  }, [organizationId, onDone]);
  return { connect, disconnect, busy, error };
}
